import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet, Plus, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { apiError, brl } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const CATS = ["Estrutura", "Kits/Camisas", "Medalhas/Troféus", "Premiação", "Marketing", "Alimentação", "Segurança/Saúde", "Taxas", "Geral"];
const METHODS = ["PIX", "Dinheiro", "Cartão", "Transferência", "Boleto"];
const empty = { description: "", category: "Geral", amount: "", payment_method: "PIX", paid_to: "", responsible: "", expense_date: new Date().toISOString().slice(0, 10), notes: "" };

export default function Expenses() {
  const { user } = useAuth();
  const [data, setData] = useState({ expenses: [], total: 0 });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...empty, responsible: user?.name || "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => api.get("/admin/expenses").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.description.trim()) return toast.error("Informe a descrição");
    if (!(parseFloat(form.amount) > 0)) return toast.error("Informe um valor válido");
    setBusy(true);
    try {
      await api.post("/admin/expenses", { ...form, amount: parseFloat(form.amount) });
      toast.success("Despesa lançada!"); setOpen(false); setForm({ ...empty, responsible: user?.name || "" }); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/admin/expenses/${id}`); toast.success("Despesa removida"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const byCat = data.expenses.reduce((acc, e) => { acc[e.category] = (acc[e.category] || 0) + e.amount; return acc; }, {});

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-brand font-bold uppercase tracking-widest text-xs">Financeiro</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black uppercase">Despesas</h1>
          <p className="text-slate-400 text-sm mt-1">Total lançado: <b className="text-red-300" data-testid="expenses-total">{brl(data.total)}</b></p>
        </div>
        <Button data-testid="expense-new-btn" onClick={() => setOpen(true)} className="bg-brand hover:bg-brand-hover font-bold uppercase tracking-wide"><Plus className="w-4 h-4 mr-1" /> Lançar Despesa</Button>
      </div>

      {Object.keys(byCat).length > 0 && (
        <div className="flex flex-wrap gap-2">
          {Object.entries(byCat).map(([c, v]) => <span key={c} className="text-xs px-3 py-1 rounded-full bg-surface border border-white/10 text-slate-300">{c}: <b className="text-white">{brl(v)}</b></span>)}
        </div>
      )}

      <div className="rounded-2xl bg-surface border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-widest text-slate-500 border-b border-white/10">
            <th className="px-4 py-3">Data</th><th className="px-4 py-3">Descrição</th><th className="px-4 py-3">Categoria</th><th className="px-4 py-3">Pago a</th><th className="px-4 py-3">Forma</th><th className="px-4 py-3">Responsável</th><th className="px-4 py-3 text-right">Valor</th><th className="px-4 py-3"></th></tr></thead>
          <tbody>
            {data.expenses.length === 0 && <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-500">Nenhuma despesa lançada.</td></tr>}
            {data.expenses.map((e) => (
              <tr key={e.id} data-testid={`expense-row-${e.id}`} className="border-b border-white/5">
                <td className="px-4 py-2 text-xs text-slate-400">{e.expense_date?.split("-").reverse().join("/")}</td>
                <td className="px-4 py-2 font-semibold">{e.description}{e.notes && <div className="text-xs text-slate-500 font-normal">{e.notes}</div>}</td>
                <td className="px-4 py-2 text-xs">{e.category}</td>
                <td className="px-4 py-2 text-xs">{e.paid_to || "—"}</td>
                <td className="px-4 py-2 text-xs">{e.payment_method}</td>
                <td className="px-4 py-2 text-xs">{e.responsible}</td>
                <td className="px-4 py-2 text-right font-mono text-red-300">{brl(e.amount)}</td>
                <td className="px-4 py-2 text-right"><button data-testid={`expense-del-${e.id}`} onClick={() => remove(e.id)} className="text-slate-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-surface border-white/10 text-white max-w-lg max-h-[92vh] overflow-y-auto" data-testid="expense-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase flex items-center gap-2"><Wallet className="w-6 h-6 text-brand" /> Nova Despesa</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-slate-300">Descrição *</Label><Input data-testid="expense-description" value={form.description} onChange={(e) => set("description", e.target.value)} className="bg-ink border-white/10 mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-300">Valor (R$) *</Label><Input data-testid="expense-amount" type="number" step="0.01" min="0" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="bg-ink border-white/10 mt-1" /></div>
              <div><Label className="text-slate-300">Data</Label><Input data-testid="expense-date" type="date" value={form.expense_date} onChange={(e) => set("expense_date", e.target.value)} className="bg-ink border-white/10 mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-300">Categoria</Label>
                <Select value={form.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger data-testid="expense-category" className="bg-ink border-white/10 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-surface border-white/10 text-white">{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-slate-300">Forma de pagamento</Label>
                <Select value={form.payment_method} onValueChange={(v) => set("payment_method", v)}>
                  <SelectTrigger data-testid="expense-method" className="bg-ink border-white/10 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-surface border-white/10 text-white">{METHODS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-300">Pago a (fornecedor)</Label><Input data-testid="expense-paid-to" value={form.paid_to} onChange={(e) => set("paid_to", e.target.value)} className="bg-ink border-white/10 mt-1" /></div>
              <div><Label className="text-slate-300">Responsável</Label><Input data-testid="expense-responsible" value={form.responsible} onChange={(e) => set("responsible", e.target.value)} className="bg-ink border-white/10 mt-1" /></div>
            </div>
            <div><Label className="text-slate-300">Observações</Label><Input data-testid="expense-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="bg-ink border-white/10 mt-1" /></div>
            <Button data-testid="expense-submit-btn" onClick={submit} disabled={busy} className="w-full bg-brand hover:bg-brand-hover font-bold uppercase tracking-wide">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar Despesa"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
